import { Box, Grid2, Typography } from "@mui/material"
import { CollapsibleCard } from "components/CollapsiblCard/CollapsibleCard"
import { Device, DeviceType, Examination, File, Project } from "graphql/types"
import { useState } from "react"
import { FileBox } from "./FileBox"

type DocumentCardProps = {
  examination: Examination
  project: Project
  showDeviceImages: boolean
  inTestReportUI?: boolean
}

export const DocumentCard = (props: DocumentCardProps) => {
  const { examination, project, showDeviceImages, inTestReportUI = false } = props

  const [open, setOpen] = useState(false)

  const gridSize = inTestReportUI ? { xs: 12, sm: 6, md: 6 } : { xs: 6, sm: 4, md: 3 }

  const getDevicesByType = (type: DeviceType): Device[] => {
    if (project) {
      let devices = project.devices.filter((item) => item.type === type)

      if (type === DeviceType.EUT_GROUP) {
        devices = devices.filter((item) =>
          (item as any).examinations?.some((exam: { id: string }) => exam.id === examination.id)
        )
      }

      return devices
    }
    return []
  }

  return (
    <Box>
      <CollapsibleCard
        open={open}
        onToggle={() => setOpen(!open)}
        title={`Dokumente & Bilder (${(() => {
          const normDocs = examination.norm.documents?.length || 0
          const projectDocs = project.documents?.length || 0
          const setupImages =
            examination.deviceSetup?.flatMap((setup) => setup.images || []).length || 0
          const taskImages =
            examination.tasks?.flatMap(
              (task) => task.testDatas?.flatMap((td) => td.image || []) || []
            ).length || 0
          // Add device documents count
          const deviceDocs = (() => {
            const examinationDevices = [
              ...getDevicesByType(DeviceType.EUT_GROUP),
              ...getDevicesByType(DeviceType.AUX),
              ...getDevicesByType(DeviceType.CONSUMABLE),
              ...getDevicesByType(DeviceType.TEST_EQUIPMENT),
            ]
            let totalDocs = 0
            examinationDevices.forEach((device) => {
              totalDocs += device.documents?.length || 0
              // For EUT groups, also check nested devices
              if (device.type === DeviceType.EUT_GROUP && (device as any).devices) {
                ;(device as any).devices.forEach((nestedDevice: any) => {
                  totalDocs += nestedDevice.documents?.length || 0
                })
              }
            })
            return totalDocs
          })()
          // Add device images count
          const deviceImages = (() => {
            const examinationDevices = [
              ...getDevicesByType(DeviceType.EUT_GROUP),
              ...getDevicesByType(DeviceType.AUX),
              ...getDevicesByType(DeviceType.CONSUMABLE),
              ...getDevicesByType(DeviceType.TEST_EQUIPMENT),
            ]
            const uniqueImageIds = new Set<string>()
            let totalImages = 0
            examinationDevices.forEach((device) => {
              device.images?.forEach((img) => {
                if (!uniqueImageIds.has(img.id)) {
                  uniqueImageIds.add(img.id)
                  totalImages++
                }
              })
              // For EUT groups, also check nested devices
              if (device.type === DeviceType.EUT_GROUP && (device as any).devices) {
                ;(device as any).devices.forEach((nestedDevice: any) => {
                  nestedDevice.images?.forEach((img: File) => {
                    if (!uniqueImageIds.has(img.id)) {
                      uniqueImageIds.add(img.id)
                      totalImages++
                    }
                  })
                })
              }
            })
            return totalImages
          })()
          return normDocs + projectDocs + setupImages + taskImages + deviceDocs + deviceImages
        })()})`}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Norm Documents */}
          {examination.norm.documents && examination.norm.documents.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Norm ({examination.norm.documents.length})
              </Typography>
              <Grid2 container spacing={2}>
                {examination.norm.documents.map((doc) => (
                  <Grid2 size={gridSize} key={doc.id}>
                    <FileBox doc={doc} inTestReportUI={inTestReportUI} examination={examination} />
                  </Grid2>
                ))}
              </Grid2>
            </Box>
          )}
          {/* Project Documents */}
          {project.documents && project.documents.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Projekt
              </Typography>
              <Grid2 container spacing={2}>
                {project.documents.map((doc) => (
                  <Grid2 size={gridSize} key={doc.id}>
                    <FileBox doc={doc} inTestReportUI={inTestReportUI} examination={examination} />
                  </Grid2>
                ))}
              </Grid2>
            </Box>
          )}

          {/* Device Images - Only show devices that are part of this examination */}
          {(() => {
            // Collect device images only from devices used in this examination
            const deviceImages: Array<{
              image: File
              deviceName: string
              deviceType: string
            }> = []

            // Track unique image IDs to prevent duplicates
            const uniqueImageIds = new Set<string>()

            // Get all device types that are shown in the equipment section above
            const examinationDevices = [
              ...getDevicesByType(DeviceType.EUT_GROUP),
              ...getDevicesByType(DeviceType.AUX),
              ...getDevicesByType(DeviceType.CONSUMABLE),
              ...getDevicesByType(DeviceType.TEST_EQUIPMENT),
            ]

            examinationDevices.forEach((device) => {
              // Add images from the device itself
              device.images?.forEach((img) => {
                // Only add if image ID is not already included
                if (!uniqueImageIds.has(img.id)) {
                  uniqueImageIds.add(img.id)
                  deviceImages.push({
                    image: img,
                    deviceName: device.name,
                    deviceType: device.type,
                  })
                }
              })

              // For EUT groups, also check nested devices
              if (device.type === DeviceType.EUT_GROUP && (device as any).devices) {
                ;(device as any).devices.forEach((nestedDevice: any) => {
                  nestedDevice.images?.forEach((img: File) => {
                    // Only add if image ID is not already included
                    if (!uniqueImageIds.has(img.id)) {
                      uniqueImageIds.add(img.id)
                      deviceImages.push({
                        image: img,
                        deviceName: `${device.name} - ${nestedDevice.name}`,
                        deviceType: "EUT",
                      })
                    }
                  })
                })
              }
            })

            return deviceImages.length > 0 && showDeviceImages ? (
              <Box>
                {/* Uncomment if you want to see device images */}

                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Gerätebilder
                </Typography>
                <Grid2 container spacing={2}>
                  {deviceImages.map(({ image }) => (
                    <Grid2 size={gridSize} key={image.id}>
                      <FileBox
                        doc={image}
                        inTestReportUI={inTestReportUI}
                        examination={examination}
                      />
                    </Grid2>
                  ))}
                </Grid2>
              </Box>
            ) : null
          })()}

          {/* Device Documents */}
          {(() => {
            // Collect device documents only from devices used in this examination
            const deviceDocuments: Array<{
              document: File
              deviceName: string
              deviceType: string
            }> = []

            // Track unique document IDs to prevent duplicates
            const uniqueDocumentIds = new Set<string>()

            // Get all device types that are shown in the equipment section above
            const examinationDevices = [
              ...getDevicesByType(DeviceType.EUT_GROUP),
              ...getDevicesByType(DeviceType.AUX),
              ...getDevicesByType(DeviceType.CONSUMABLE),
              ...getDevicesByType(DeviceType.TEST_EQUIPMENT),
            ]

            examinationDevices.forEach((device) => {
              // Add documents from the device itself
              device.documents?.forEach((doc) => {
                // Only add if document ID is not already included
                if (!uniqueDocumentIds.has(doc.id)) {
                  uniqueDocumentIds.add(doc.id)
                  deviceDocuments.push({
                    document: doc,
                    deviceName: device.name,
                    deviceType: device.type,
                  })
                }
              })

              // For EUT groups, also check nested devices
              if (device.type === DeviceType.EUT_GROUP && (device as any).devices) {
                ;(device as any).devices.forEach((nestedDevice: any) => {
                  nestedDevice.documents?.forEach((doc: File) => {
                    // Only add if document ID is not already included
                    if (!uniqueDocumentIds.has(doc.id)) {
                      uniqueDocumentIds.add(doc.id)
                      deviceDocuments.push({
                        document: doc,
                        deviceName: `${device.name} - ${nestedDevice.name}`,
                        deviceType: "EUT",
                      })
                    }
                  })
                })
              }
            })

            return deviceDocuments.length > 0 ? (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Gerätedokumente
                </Typography>
                <Grid2 container spacing={2}>
                  {deviceDocuments.map(({ document }) => (
                    <Grid2 size={gridSize} key={document.id}>
                      <FileBox
                        doc={document}
                        inTestReportUI={inTestReportUI}
                        examination={examination}
                      />
                    </Grid2>
                  ))}
                </Grid2>
              </Box>
            ) : null
          })()}

          {/* Device Setup Images */}
          {examination.deviceSetup &&
            examination.deviceSetup.length > 0 &&
            (() => {
              const setupImages = examination.deviceSetup.flatMap((setup) =>
                (setup.images ?? []).map((img) => ({
                  ...img,
                  deviceName: setup.device?.name || "Unknown Device",
                  setupNote: setup.note || "",
                }))
              )
              return (
                setupImages.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      Geräteaufbau Bilder
                    </Typography>
                    <Grid2 container spacing={2}>
                      {setupImages.map((doc) => (
                        <Grid2 size={gridSize} key={doc.id}>
                          <FileBox
                            doc={doc}
                            inTestReportUI={inTestReportUI}
                            examination={examination}
                          />
                        </Grid2>
                      ))}
                    </Grid2>
                  </Box>
                )
              )
            })()}

          {/* Task Documents/Images */}
          {examination.tasks &&
            examination.tasks.length > 0 &&
            (() => {
              // Debug logging to understand image structure
              //   console.log("Examination data:", examination)
              //   console.log("Device setup:", examination.deviceSetup)
              //   console.log("Examination tasks:", examination.tasks)
              //   examination.tasks.forEach((task, taskIdx) => {
              //     console.log(`Task ${taskIdx} (${task.name}):`, task)
              //     if (task.testDatas) {
              //       task.testDatas.forEach((testData, tdIdx) => {
              //         console.log(`  TestData ${tdIdx} (${testData.name}):`, testData)
              //         console.log(`    Images:`, testData.image)
              //       })
              //     }
              //   })

              const taskImages = (examination.tasks ?? []).flatMap((task) =>
                (task?.testDatas ?? []).flatMap((testData) =>
                  (testData?.image ?? []).map((img) => ({
                    ...img,
                    taskName: task.name,
                    testDataName: testData.name,
                  }))
                )
              )

              //   console.log("Flattened task images:", taskImages)

              return (
                taskImages.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      Fotos aus Arbeitsschritten
                    </Typography>
                    <Grid2 container spacing={2}>
                      {taskImages.map((doc) => (
                        <Grid2 size={gridSize} key={doc.id}>
                          <FileBox
                            doc={doc}
                            inTestReportUI={inTestReportUI}
                            examination={examination}
                          />
                        </Grid2>
                      ))}
                    </Grid2>
                  </Box>
                )
              )
            })()}

          {/* Show message when no documents */}
          {(() => {
            const hasNormDocuments =
              examination.norm.documents && examination.norm.documents.length > 0
            const hasProjectDocuments = project.documents && project.documents.length > 0
            const taskImages =
              examination.tasks?.flatMap((task) =>
                task.testDatas.flatMap((testData) => testData.image)
              ) || []
            const hasTaskDocuments = taskImages.length > 0
            const hasSetupImages =
              examination.deviceSetup?.flatMap((setup) => setup.images || []).length > 0

            // Check for device documents and images
            const hasDeviceDocuments = (() => {
              const examinationDevices = [
                ...getDevicesByType(DeviceType.EUT_GROUP),
                ...getDevicesByType(DeviceType.AUX),
                ...getDevicesByType(DeviceType.CONSUMABLE),
                ...getDevicesByType(DeviceType.TEST_EQUIPMENT),
              ]
              return examinationDevices.some((device) => {
                if (device.documents && device.documents.length > 0) return true
                if (device.images && device.images.length > 0) return true
                if (device.type === DeviceType.EUT_GROUP && (device as any).devices) {
                  return (device as any).devices.some(
                    (nestedDevice: any) =>
                      (nestedDevice.documents && nestedDevice.documents.length > 0) ||
                      (nestedDevice.images && nestedDevice.images.length > 0)
                  )
                }
                return false
              })
            })()

            return (
              !hasNormDocuments &&
              !hasProjectDocuments &&
              !hasTaskDocuments &&
              !hasSetupImages &&
              !hasDeviceDocuments && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "center", py: 2 }}
                >
                  Keine Dokumente & Bilder vorhanden.
                </Typography>
              )
            )
          })()}
        </Box>
      </CollapsibleCard>
    </Box>
  )
}
